import { useState } from "react";
import { Link } from "react-router-dom";
import { buildUrl, useApi, useDebounced } from "../api";
import type { Counted, Paged, Party, Promise_ } from "../api";
import {
  COMMITMENT_LABEL,
  Empty,
  ErrorNote,
  Loading,
  Pager,
  PartyTag,
  StatusBadge,
} from "../components";

const LIMIT = 25;

interface AreaRow extends Counted {
  policy_area: string;
}

export default function Promises() {
  const [query, setQuery] = useState("");
  const [party, setParty] = useState("");
  const [area, setArea] = useState("");
  const [commitment, setCommitment] = useState("");
  const [offset, setOffset] = useState(0);
  const search = useDebounced(query);

  const parties = useApi<Party[]>(buildUrl("/api/parties"));
  const areas = useApi<AreaRow[]>(buildUrl("/api/policy-areas"));
  const { data, loading, error, reload } = useApi<Paged<Promise_>>(
    buildUrl("/api/promises", {
      q: search || null,
      party: party || null,
      policy_area: area || null,
      commitment: commitment || null,
      limit: LIMIT,
      offset,
    }),
  );

  function change<T>(setter: (v: T) => void) {
    return (value: T) => {
      setter(value);
      setOffset(0);
    };
  }

  return (
    <>
      <div className="page-head">
        <h1>Wahlversprechen</h1>
        <p>
          Einzelne prüfbare Zusagen aus Wahlprogrammen und Koalitionsvertrag. Zu jedem
          Versprechen ist festgehalten, wie fest die Zusage formuliert war und wie gut
          sich ihre Erfüllung überhaupt feststellen lässt.
        </p>
      </div>

      <div className="filters">
        <input
          type="search"
          placeholder="Suche im Wortlaut"
          value={query}
          onChange={(e) => change(setQuery)(e.target.value)}
          aria-label="Versprechen durchsuchen"
        />
        <select
          value={party}
          onChange={(e) => change(setParty)(e.target.value)}
          aria-label="Nach Partei filtern"
        >
          <option value="">Alle Parteien</option>
          {(parties.data ?? [])
            .filter((p) => p.versprechen > 0)
            .map((p) => (
              <option key={p.id} value={p.short_name}>
                {p.short_name} ({p.versprechen})
              </option>
            ))}
        </select>
        <select
          value={area}
          onChange={(e) => change(setArea)(e.target.value)}
          aria-label="Nach Themenfeld filtern"
        >
          <option value="">Alle Themen</option>
          {(areas.data ?? []).map((a) => (
            <option key={a.policy_area} value={a.policy_area}>
              {a.policy_area} ({a.anzahl})
            </option>
          ))}
        </select>
        <select
          value={commitment}
          onChange={(e) => change(setCommitment)(e.target.value)}
          aria-label="Nach Verbindlichkeit filtern"
        >
          <option value="">Jede Verbindlichkeit</option>
          <option value="fest">feste Zusage</option>
          <option value="absicht">Absicht</option>
          <option value="pruefauftrag">Prüfauftrag</option>
          <option value="vage">vage</option>
        </select>
      </div>

      {error ? (
        <ErrorNote message={error} onRetry={reload} />
      ) : loading && !data ? (
        <Loading rows={8} />
      ) : !data || data.items.length === 0 ? (
        <Empty>
          {data?.total === 0 && !search && !party && !area ? (
            <>
              Noch keine Versprechen erfasst. Die Programme sind geladen, aber die
              Auswertung ist noch nicht gelaufen.
            </>
          ) : (
            <>Keine Versprechen gefunden. Filter zurücksetzen oder Suchbegriff ändern.</>
          )}
        </Empty>
      ) : (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: "7rem" }}>Partei</th>
                  <th>Versprechen</th>
                  <th style={{ width: "8rem" }}>Verbindlich</th>
                  <th style={{ width: "9rem" }}>Bewertung</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <PartyTag short={p.short_name} color={p.color} />
                      <div
                        style={{
                          fontSize: "0.76rem",
                          color: "var(--ink-3)",
                          marginTop: "0.2rem",
                        }}
                      >
                        {p.programm_art === "koalitionsvertrag" ? "KoaV" : "Programm"}
                      </div>
                    </td>
                    <td>
                      <Link to={`/versprechen/${p.id}`} style={{ fontWeight: 500 }}>
                        {p.normalized}
                      </Link>
                      <div
                        style={{
                          fontSize: "0.82rem",
                          color: "var(--ink-3)",
                          marginTop: "0.15rem",
                          display: "flex",
                          gap: "0.4rem",
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <span>
                          {p.policy_area}
                          {p.page_number ? ` · S. ${p.page_number}` : ""}
                          {p.belege > 0 ? ` · ${p.belege} Belege` : ""}
                        </span>
                        {p.quote_verified_at ? (
                          <span
                            className="pill"
                            title={`Wortlaut im Originaldokument gefunden${
                              p.quote_found_page ? `, Seite ${p.quote_found_page}` : ""
                            }`}
                          >
                            ✓ Zitat belegt
                          </span>
                        ) : (
                          <span className="pill" title="Wortlaut nicht im PDF bestätigt">
                            Zitat ungeprüft
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="pill">
                        {COMMITMENT_LABEL[p.commitment ?? ""] ?? p.commitment ?? "–"}
                      </span>
                    </td>
                    <td>
                      <StatusBadge status={p.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager offset={offset} limit={LIMIT} total={data.total} onChange={setOffset} />
        </>
      )}
    </>
  );
}
